import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  structuredData?: object;
}

const SEOHead = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = "https://verifyprop.com/android-chrome-512x512.png",
  ogType = "website",
  noIndex = false,
  structuredData
}: SEOHeadProps) => {
  const location = useLocation();

  useEffect(() => {
    // Update page title
    if (title) {
      document.title = title;
    }

    // Update meta description
    if (description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      }
    }

    // Update meta keywords
    if (keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords);
      }
    }

    // Update canonical URL
    const canonicalUrl = canonical || `https://verifyprop.com${location.pathname}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const ogImageTag = document.querySelector('meta[property="og:image"]');
    const ogTypeTag = document.querySelector('meta[property="og:type"]');

    if (ogTitle && title) ogTitle.setAttribute('content', title);
    if (ogDescription && description) ogDescription.setAttribute('content', description);
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
    if (ogImageTag) ogImageTag.setAttribute('content', ogImage);
    if (ogTypeTag) ogTypeTag.setAttribute('content', ogType);

    // Update Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    const twitterImage = document.querySelector('meta[name="twitter:image"]');

    if (twitterTitle && title) twitterTitle.setAttribute('content', title);
    if (twitterDescription && description) twitterDescription.setAttribute('content', description);
    if (twitterUrl) twitterUrl.setAttribute('content', canonicalUrl);
    if (twitterImage) twitterImage.setAttribute('content', ogImage);

    // Update robots tag
    const robotsTag = document.querySelector('meta[name="robots"]');
    if (robotsTag) {
      robotsTag.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow');
    }

    // Add structured data
    if (structuredData) {
      // Remove existing structured data scripts
      const existingScripts = document.querySelectorAll('script[data-type="structured-data"]');
      existingScripts.forEach(script => script.remove());
      
      // Handle multiple schemas
      const schemas = Array.isArray(structuredData) ? structuredData : [structuredData];
      
      schemas.forEach((schema, index) => {
        const structuredDataScript = document.createElement('script');
        structuredDataScript.setAttribute('type', 'application/ld+json');
        structuredDataScript.setAttribute('data-type', 'structured-data');
        structuredDataScript.setAttribute('data-index', index.toString());
        structuredDataScript.textContent = JSON.stringify(schema);
        document.head.appendChild(structuredDataScript);
      });
    }
  }, [title, description, keywords, canonical, ogImage, ogType, noIndex, structuredData, location.pathname]);

  return null;
};

export default SEOHead;